import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { useApiClient } from 'app/data/api-client';
import { Building, GetBuildingAPIResponse, GetBuildingQueryResponse } from 'app/data/types/building';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';
import { retryOnAuthError } from 'app/shared/auth';

export const BUILDING_QUERY_KEY = 'building';

export const getUseBuildingQueryKey = (buildingUuid: string): QueryKey => [BUILDING_QUERY_KEY, buildingUuid];

/**
 * Fetches the user's building. Skipped when `sdkBuilding` is already
 * provided by the host frame's initResponse — that's the common path.
 */
export const useBuildingQuery = (sdkBuilding?: Building | null): GetBuildingQueryResponse => {
  const client = useApiClient();
  const { apiUrl } = useApi();
  const { addGenericError, hideGenericErrors } = useErrors();
  const { userBuildingUuid } = useOpenParams();

  const { data, error, isLoading, isSuccess } = useQuery<Building | null, Error>({
    queryKey: getUseBuildingQueryKey(userBuildingUuid),
    queryFn: async () => {
      const envelope = await client.get<GetBuildingAPIResponse>(
        `/api/tenant/v1/buildings/${userBuildingUuid}`,
      );
      return envelope?.data?.building ?? null;
    },
    enabled: !!userBuildingUuid && !!apiUrl && !sdkBuilding,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      addGenericError(BUILDING_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(BUILDING_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    building: sdkBuilding ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
